import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Load Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const SavingsTracker = ({ isSubscribed = false }) => {
  const [selectedAmount, setSelectedAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [email, setEmail] = useState('');
  const [goalAmount, setGoalAmount] = useState('');
  const [startOption, setStartOption] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSetup, setIsSetup] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isSubscribed) {
      setIsSetup(true);
    }
  }, [isSubscribed]);

  // Get tomorrow's date for min date attribute
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  const PRESET_AMOUNTS = [
    { value: '25', label: '$25/month' },
    { value: '50', label: '$50/month' },
    { value: '100', label: '$100/month' },
    { value: '200', label: '$200/month' }
  ];

  const calculateFeeAndTotal = (baseAmount) => {
    const fee = (baseAmount * 5) / 100;
    const total = baseAmount + fee;
    return { fee, total };
  };

  const calculateGoalAchievementDate = (monthlyAmount, goalAmount) => {
    if (!goalAmount || !monthlyAmount) return null;
    const monthsToGoal = Math.ceil(goalAmount / monthlyAmount);
    const achievementDate = new Date();
    achievementDate.setMonth(achievementDate.getMonth() + monthsToGoal);
    return achievementDate;
  };

  // Function to check if a user already exists
  const checkUserExists = async (email) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/auth/check-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      return data.exists;
    } catch (error) {
      console.error('Error checking user:', error);
      return false; // Assume user doesn't exist if error occurs
    }
  };

  const handleSetupGoal = async () => {
    const monthlyAmount = selectedAmount === 'custom' ? customAmount : selectedAmount;
    const baseAmount = parseFloat(monthlyAmount);
    const { total } = calculateFeeAndTotal(baseAmount);

    if (monthlyAmount && email && acceptedTerms) {
      setIsProcessing(true);
      try {
        // Check if user with this email already exists
        const userExists = await checkUserExists(email);
        
        if (userExists) {
          // If user exists, show login prompt instead of proceeding
          setIsProcessing(false);
          setShowLoginPrompt(true);
          return;
        }

        // Define API URL with fallback
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        console.log('Using API URL:', apiUrl);

        const requestBody = {
          amount: total,
          email: email,
          startDate: startOption === 'future' ? startDate : new Date().toISOString().split('T')[0],
          goalAmount: goalAmount ? parseFloat(goalAmount) : undefined,
        };

        console.log('Request details:', {
          url: `${apiUrl}/api/stripe/setup-payment`,
          body: requestBody
        });

        const response = await fetch(`${apiUrl}/api/stripe/setup-payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        console.log('Response status:', response.status);

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error response:', errorData);
          
          // Check if error is due to existing user
          if (response.status === 409) {
            setShowLoginPrompt(true);
            return;
          }
          
          throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
        }

        const data = await response.json();
        console.log('Success response:', data);

        if (!data.sessionId) {
          throw new Error('No session ID received from server');
        }

        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Failed to initialize Stripe');
        }

        const { error } = await stripe.redirectToCheckout({
          sessionId: data.sessionId
        });
        
        if (error) {
          console.error('Stripe redirect error:', error);
          throw error;
        }
      } catch (error) {
        console.error('Payment setup error:', error);
        alert('Failed to start payment process: ' + (error.message || 'Unknown error'));
      } finally {
        setIsProcessing(false);
      }
    } else {
      console.log('Validation failed:', { monthlyAmount, email, acceptedTerms });
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
      {/* Login Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => navigate('/login')}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Login
        </button>
      </div>

      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Squadtrip Saver</h1>
        <p className="text-gray-600 mt-2">Start your journey to your dream vacation</p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Choose your monthly savings amount:
          </label>
          <div className="grid grid-cols-2 gap-4 mb-4">
            {PRESET_AMOUNTS.map((amount) => (
              <button
                key={amount.value}
                onClick={() => setSelectedAmount(amount.value)}
                className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                  selectedAmount === amount.value
                    ? 'border-blue-500 bg-blue-50 transform scale-105'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
              >
                <div className="font-medium text-gray-900">{amount.label}</div>
              </button>
            ))}
          </div>
          <div className="mt-4">
            <input
              type="number"
              placeholder="Enter custom amount"
              className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onChange={(e) => {
                setSelectedAmount('custom');
                setCustomAmount(e.target.value);
              }}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your email address:
          </label>
          <input
            type="email"
            className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            What's your total savings goal? (Optional)
          </label>
          <input
            type="number"
            className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter total goal amount"
            value={goalAmount}
            onChange={(e) => setGoalAmount(e.target.value)}
          />
          {goalAmount && selectedAmount && (
            <div className="mt-2 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                At ${selectedAmount === 'custom' ? customAmount : selectedAmount}/month, 
                you'll reach your goal of ${goalAmount} by{' '}
                {calculateGoalAchievementDate(
                  parseFloat(selectedAmount === 'custom' ? customAmount : selectedAmount),
                  parseFloat(goalAmount)
                ).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            When would you like to start saving?
          </label>
          <div className="space-y-3">
            <label className="flex items-center space-x-3 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="startOption"
                value="today"
                checked={startOption === 'today'}
                onChange={(e) => setStartOption(e.target.value)}
                className="h-4 w-4 text-blue-500 focus:ring-blue-500"
              />
              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                <span>Start today</span>
              </div>
            </label>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="startOption"
                  value="future"
                  checked={startOption === 'future'}
                  onChange={(e) => setStartOption(e.target.value)}
                  className="h-4 w-4 text-blue-500 focus:ring-blue-500"
                />
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                  <span>Start on a future date</span>
                </div>
              </label>
              {startOption === 'future' && (
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  min={minDate}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mt-2"
                />
              )}
            </div>
          </div>

          {/* Charging Schedule Message */}
          {selectedAmount && (startOption === 'today' || (startOption === 'future' && startDate)) && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                {(() => {
                  const baseAmount = selectedAmount === 'custom' ? parseFloat(customAmount) : parseFloat(selectedAmount);
                  const { fee, total } = calculateFeeAndTotal(baseAmount);
                  return (
                    <>
                      Monthly breakdown:
                      <br />
                      • Savings amount: ${baseAmount.toFixed(2)}
                      <br />
                      • Transaction fee (5%): ${fee.toFixed(2)}
                      <br />
                      • Total monthly charge: ${total.toFixed(2)}
                      <br /><br />
                      Your card will be charged ${total.toFixed(2)} on {startOption === 'today' 
                        ? new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
                        : new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} 
                      and will continue to be charged on the {startOption === 'today' 
                        ? new Date().getDate() 
                        : new Date(startDate + 'T00:00:00').getDate()}{getOrdinalSuffix(startOption === 'today' 
                          ? new Date().getDate() 
                          : new Date(startDate + 'T00:00:00').getDate())} of each month.
                    </>
                  );
                })()}
              </p>
            </div>
          )}
        </div>

        {/* Terms and Conditions Checkbox */}
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="terms"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-1 h-4 w-4 text-blue-500 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="terms" className="text-sm text-gray-600">
            I accept the{' '}
            <a href="/terms" className="text-blue-500 hover:text-blue-600 underline">
              Terms and Conditions
            </a>{' '}
            and understand that my card will be charged monthly for the selected amount.
          </label>
        </div>

        <button
          onClick={handleSetupGoal}
          disabled={
            isProcessing || 
            !selectedAmount || 
            !email || 
            !acceptedTerms ||
            (selectedAmount === 'custom' && !customAmount) ||
            (startOption === 'future' && !startDate)
          }
          className={`w-full p-4 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
            isProcessing || !selectedAmount || !email || !acceptedTerms || (selectedAmount === 'custom' && !customAmount) || (startOption === 'future' && !startDate)
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isProcessing ? 'Processing...' : 'Start Saving'}
        </button>
      </div>

      {/* Account Already Exists Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Account Already Exists</h3>
            <p className="mb-4">An account with this email already exists. Would you like to log in?</p>
            <div className="flex space-x-4">
              <button
                onClick={() => navigate('/login')}
                className="flex-1 bg-blue-600 text-white p-2 rounded hover:bg-blue-700"
              >
                Log In
              </button>
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="flex-1 bg-gray-200 p-2 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function for ordinal suffixes
const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

export default SavingsTracker;